import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EQUIPMENT_TYPES } from "@/lib/status";
import { Plus, Wind, Package, Trash2, FileText, Loader2, Hash } from "lucide-react";
import { toast } from "sonner";
import EquipmentMediaFields from "@/components/app/EquipmentMediaFields";

const EMPTY = { equipment_type: "", brand: "", model: "", serial_number: "", location: "", install_date: "", qr_code: "", photo: "" };

export default function CustomerEquipmentTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [invoice, setInvoice] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/equipment");
      setItems(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (open) { setForm(EMPTY); setInvoice(null); } }, [open]);

  const handleInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoice(data.path);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar nota fiscal");
    } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.equipment_type) { toast.error("Selecione o tipo do equipamento."); return; }
    if (!form.serial_number.trim() && !form.brand.trim()) {
      toast.error("Informe ao menos a marca ou o nº de série.");
      return;
    }
    if (!form.photo) { toast.error("Envie a foto do equipamento."); return; }
    setBusy(true);
    try {
      const { qr_code, ...rest } = form;
      await api.post("/customer/equipment", { ...rest, ...(qr_code.trim() ? { qr_code: qr_code.trim() } : {}), invoice: invoice || null });
      toast.success("Equipamento cadastrado!");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/customer/equipment/${id}`);
      toast.success("Equipamento removido.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="add-equipment-button">
              <Plus className="h-4 w-4" /> Cadastrar equipamento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="equipment-dialog">
            <DialogHeader><DialogTitle>Cadastrar equipamento</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Tipo *</Label>
                <Select value={form.equipment_type} onValueChange={(v) => setForm({ ...form, equipment_type: v })}>
                  <SelectTrigger data-testid="equipment-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`equipment-type-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Marca</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Ex: Elgin" data-testid="equipment-brand-input" /></div>
                <div className="space-y-1.5"><Label>Modelo</Label>
                  <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Ex: Eco 12000" data-testid="equipment-model-input" /></div>
              </div>
              <div className="space-y-1.5"><Label>Nº de série</Label>
                <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="Número de série" data-testid="equipment-serial-input" /></div>
              <div className="space-y-1.5"><Label>Local (opcional)</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex: Sala, quarto..." data-testid="equipment-location-input" /></div>
              <EquipmentMediaFields qrCode={form.qr_code} onQr={(v) => setForm((f) => ({ ...f, qr_code: v }))}
                photo={form.photo} onPhoto={(v) => setForm((f) => ({ ...f, photo: v }))} />
              <div className="space-y-1.5">
                <Label>Nota fiscal (PDF/imagem)</Label>
                {invoice ? (
                  <a href={fileUrl(invoice)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 underline" data-testid="equipment-invoice-preview">
                    <FileText className="h-4 w-4" /> Nota fiscal enviada
                  </a>
                ) : (
                  <label className="flex items-center gap-2 rounded-md border-2 border-dashed border-slate-300 px-3 py-2 cursor-pointer hover:border-blue-400 text-slate-500 text-sm" data-testid="equipment-invoice-label">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Enviar nota fiscal
                    <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={handleInvoice} data-testid="equipment-invoice-input" />
                  </label>
                )}
              </div>
              <DialogFooter>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="equipment-save-button">
                  {busy ? "Salvando..." : "Salvar equipamento"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="equipment-empty">
          <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Você ainda não cadastrou nenhum equipamento.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="customer-equipment-list">
          {items.map((eq) => (
            <Card key={eq.id} className="border-slate-200" data-testid={`customer-equipment-${eq.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {eq.photo ? <img src={fileUrl(eq.photo)} alt="Aparelho" className="h-full w-full object-cover" data-testid={`customer-equipment-photo-${eq.id}`} />
                      : <Wind className="h-5 w-5 text-[#1e7be0]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800">{eq.equipment_type}{eq.brand ? ` · ${eq.brand}` : ""}{eq.model ? ` ${eq.model}` : ""}</p>
                    {eq.serial_number && <p className="text-sm text-slate-500 flex items-center gap-1"><Hash className="h-3.5 w-3.5" />{eq.serial_number}</p>}
                    {eq.location && <p className="text-xs text-slate-400">{eq.location}</p>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => remove(eq.id)} data-testid={`remove-equipment-${eq.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {eq.invoice && (
                  <a href={fileUrl(eq.invoice)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-md px-2 py-1 w-fit hover:bg-slate-50">
                    <FileText className="h-3.5 w-3.5 text-[#1e7be0]" /> Nota fiscal
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
